import { useContext } from "react";
import { DrawingContext } from "../contexts/DrawingContext";
import { StrategyContext } from "../contexts/StrategyContext";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "../utils/api";
import { ApiResponse, ApiError } from "../types/api";
import { Project } from "../types/project";
import * as geojson from "geojson";
import { isFlammableFieldValue } from "../utils/geojsonUtils";
import { isBuildingAssetType } from "./constructionTypes";
import { bumpVegetationTileVersion } from "./vegetation";

/** Hard-coded fallback used by the legacy "flammable field" upload path
 *  while the bulk-import UI still picks "is flammable" as Wood vs Masonry.
 *  In the new world both branches resolve to a real ConstructionType id;
 *  callers pass the wood/masonry type ids via `flammableTrueTypeId` /
 *  `flammableFalseTypeId` on the form payload. */
function _coerceConstructionTypeId(formData: any, properties?: any): number | null {
  if (formData.flammableField && properties) {
    const isWood = isFlammableFieldValue(properties?.[formData.flammableField]);
    if (isWood && formData.flammableTrueTypeId) return formData.flammableTrueTypeId;
    if (!isWood && formData.flammableFalseTypeId) return formData.flammableFalseTypeId;
  }
  return formData.constructionTypeId ?? null;
}

const TILE_POLL_INTERVAL_MS = 3000;

/** When a building asset is created (or becomes a building) in an urban
 *  project, the backend stamps its footprint into the vegetation layer as
 *  "Building" (#204). Refresh the vegetation-derived state the same way a manual
 *  vegetation edit does (see useApplyVegetationEdit in vegetationEdits.ts). */
const refreshVegetationAfterUrbanStamp = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | undefined,
  assetType: unknown,
  refreshStrategy: (() => void) | undefined
) => {
  if (!projectId || !isBuildingAssetType(assetType)) return;
  const project = queryClient.getQueryData<ApiResponse<Project>>(["project", projectId]);
  if (!project?.data?.model_urban_environment) return;
  queryClient.invalidateQueries({ queryKey: ["vegetationSources"] });
  queryClient.invalidateQueries({ queryKey: ["strategy"] });
  queryClient.invalidateQueries({ queryKey: ["strategyJob"] });
  queryClient.invalidateQueries({ queryKey: ["strategyStatus"] });
  bumpVegetationTileVersion(queryClient, projectId);
  refreshStrategy?.();
};

export const useAssetTilesUrl = (projectId: string | undefined, polling: boolean = false) => {
  return useQuery({
    queryKey: [projectId, "asset-tiles-url"],
    queryFn: () =>
      api.request<ApiResponse<{ tiles_url: string | null }>>(
        `/api/projects/${projectId}/assets/tiles-url/`
      ),
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    // A 404 means the project was deleted under an open tab: no retries and
    // no further polling (fisci-front-end #397).
    retry: (count, err) => (err as { status?: number } | undefined)?.status !== 404 && count < 3,
    refetchInterval: (query) =>
      polling && !query.state.error ? TILE_POLL_INTERVAL_MS : false,
  });
};

export const useAssets = (projectId: string | undefined, enabled: boolean = true) => {
  const {
    data: assets,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [projectId, "assets"],
    queryFn: () =>
      api.request<ApiResponse<any>>(`/api/projects/${projectId}/assets`),
    enabled: !!projectId && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  }) as {
    data: ApiResponse<any>;
    isLoading: boolean;
    error: ApiError;
    refetch: () => Promise<any>;
  };

  return { assets, isLoading, error, refetch };
};

export const useAddAsset = (projectId: string) => {
  const queryClient = useQueryClient();
  const { geojson } = useContext(DrawingContext);
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<any>, ApiError, any>({
    mutationFn: (formData: any) => {
      const newGeojson = {
        ...geojson,
        features: geojson.features.map((feature: geojson.Feature) => {
          const constructionTypeId = _coerceConstructionTypeId(formData, feature.properties);
          return {
            ...feature,
            properties: {
              name: formData.name,
              value: formData.value,
              assetType: formData.assetType,
              // Uploaded line/point features carry a per-feature width stamped
              // by the width dialog; it wins over the form-level width.
              width: (feature.properties as any)?.width ?? formData.width,
              constructionTypeId,
              extraConstructionSettingIds:
                formData.extraConstructionSettingIds ?? [],
            },
          };
        }),
      };

      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/assets`,
        {
          method: "POST",
          body: { geojson: newGeojson },
        }
      );
    },
    onSuccess: (_, formData) => {
      queryClient.invalidateQueries({ queryKey: [projectId, "assets"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-counts"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
      refreshVegetationAfterUrbanStamp(queryClient, projectId, formData?.assetType, refreshStrategy);
    },
  });
};

export const useUpdateAssetById = (projectId: string) => {
  const queryClient = useQueryClient();
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<any>, ApiError, { assetId: string | number; formData: any }>({
    mutationFn: ({ assetId, formData }) => {
      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/assets/${assetId}/`,
        {
          method: "PATCH",
          body: {
            name: formData.name,
            value: formData.value,
            assetType: formData.assetType,
            width: formData.width,
            constructionTypeId: formData.constructionTypeId ?? null,
            extraConstructionSettingIds:
              formData.extraConstructionSettingIds ?? [],
          },
        }
      );
    },
    onSuccess: (_, { formData }) => {
      queryClient.invalidateQueries({ queryKey: [projectId, "assets"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-counts"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
      refreshVegetationAfterUrbanStamp(queryClient, projectId, formData?.assetType, refreshStrategy);
    },
  });
};

export const useUploadAssets = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<any>, ApiError, { features: geojson.Feature[] }>({
    mutationFn: async ({ features }) => {
      const geojsonFC = {
        type: "FeatureCollection",
        features,
      } as geojson.FeatureCollection;
      return api.request<ApiResponse<any>>(`/api/projects/${projectId}/assets`, {
        method: "POST",
        body: { geojson: geojsonFC },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "assets"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-counts"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
    },
  });
};

export const useDeleteAllAssets = (projectId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<any>, ApiError, void>({
    mutationFn: async () => {
      const response: ApiResponse<any> = await api.request(
        `/api/projects/${projectId}/assets`,
        {
          method: "DELETE",
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "assets"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-counts"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
    },
  });
};

export const useDeleteAsset = (projectId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<any>, ApiError, string | number | undefined>({
    mutationFn: async (assetId: string | number | undefined) => {
      const response: ApiResponse<any> = await api.request(
        `/api/projects/${projectId}/assets/${assetId}/`,
        {
          method: "DELETE",
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "assets"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-counts"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
    },
  });
};

export const useUpdateAsset = (projectId: string, assetId: string | number) => {
  const queryClient = useQueryClient();
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<any>, ApiError, any>({
    mutationFn: (formData: any) => {
      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/assets/${assetId}/`,
        {
          method: "PATCH",
          body: {
            name: formData.name,
            value: formData.value,
            assetType: formData.assetType,
            width: formData.width,
            constructionTypeId: formData.constructionTypeId ?? null,
            extraConstructionSettingIds:
              formData.extraConstructionSettingIds ?? [],
          },
        }
      );
    },
    onSuccess: (_, formData) => {
      queryClient.invalidateQueries({ queryKey: [projectId, "assets"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-counts"] });
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
      refreshVegetationAfterUrbanStamp(queryClient, projectId, formData?.assetType, refreshStrategy);
    },
  });
};

export const useAssetCounts = (projectId: string | undefined) => {
  return useQuery({
    queryKey: [projectId, "asset-counts"],
    queryFn: () =>
      api.request<ApiResponse<{ total: number; by_type: { asset_type: string; count: number }[] }>>(
        `/api/projects/${projectId}/assets/count/`
      ),
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTriggerAssetTilesRegeneration = (projectId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<any>, ApiError, void>({
    mutationFn: async () => {
      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/assets/regenerate-tiles/`,
        {
          method: "POST",
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "asset-tiles-url"] });
    },
  });
};
